/**
 * Admin Retailers — full directory.
 *
 * Plain admins see the table read-only; super admins can add, edit, and
 * delete retailers and manage which products each carries. Eligibility for
 * retailer-priced estimates is computed against the required-acronym set
 * defined in /admin/settings.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2, Plus, Pencil, Search, Loader2, AlertTriangle,
  ShieldCheck, ShieldAlert, Sparkles,
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { AdminRetailerEditModal } from './AdminRetailerEditModal';
import {
  AdminRetailer,
  brandIconFor,
  deleteAdminRetailer,
  fetchAdminRetailers,
} from '../../services/adminRetailersApi';

export const AdminRetailers: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const [retailers, setRetailers] = useState<AdminRetailer[]>([]);
  const [requiredAcronyms, setRequiredAcronyms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminRetailers();
      setRetailers(data.retailers);
      setRequiredAcronyms(data.requiredAcronyms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load retailers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return retailers;
    return retailers.filter(r =>
      r.name.toLowerCase().includes(q) ||
      (r.city ?? '').toLowerCase().includes(q) ||
      (r.stateProvince ?? '').toLowerCase().includes(q) ||
      (r.country ?? '').toLowerCase().includes(q),
    );
  }, [retailers, search]);

  const handleDelete = async (id: number, name: string) => {
    setLoading(true);
    try {
      await deleteAdminRetailer(id, name);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <div>
            <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <Building2 size={22} /> Retailers
            </h2>
            <p className="text-muted small mb-0">
              Retailer directory and product assignments.
              {!isSuperAdmin && <span className="ms-2 badge text-bg-secondary">Read-only</span>}
            </p>
          </div>
          {isSuperAdmin && (
            <button
              type="button"
              className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
              onClick={() => setCreateOpen(true)}
            >
              <Plus size={14} /> Add Retailer
            </button>
          )}
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ maxWidth: 360 }}>
            <span className="input-group-text"><Search size={14} /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search name, city, state, country…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => setSearch('')}>Clear</button>
            )}
          </div>
          <span className="text-muted small ms-auto">
            {filtered.length} of {retailers.length} retailer{retailers.length === 1 ? '' : 's'}
            {requiredAcronyms.length > 0 && (
              <span className="ms-2">· Required acronyms: <code>{requiredAcronyms.join(', ')}</code></span>
            )}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover mb-0 align-middle">
            <thead className="table-tc-slate">
              <tr>
                <th>ID</th>
                <th style={{ width: 40 }} aria-label="Brand icon" />
                <th>Name</th>
                <th>Location</th>
                <th className="text-center">National</th>
                <th className="text-center">Active</th>
                <th className="text-end">Products</th>
                <th>Eligibility</th>
                <th>Partner</th>
                <th className="text-center" style={{ width: 60 }}>Edit</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {loading ? (
                <tr><td colSpan={10} className="text-center py-4 text-muted">
                  <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} /> Loading…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-4 text-muted">No retailers match.</td></tr>
              ) : filtered.map(r => {
                const elig = r.eligibility;
                return (
                  <tr key={r.id} className={r.isActive ? '' : 'text-muted'}>
                    <td className="font-monospace small">{r.id}</td>
                    <td className="text-center p-1">
                      {brandIconFor(r.id, r.brandIconUrl) && (
                        <img src={brandIconFor(r.id, r.brandIconUrl) ?? undefined} alt="" title={r.name} width={24} height={24}
                          style={{ objectFit: 'contain', borderRadius: 4, display: 'block', margin: '0 auto' }} />
                      )}
                    </td>
                    <td>
                      <div className="fw-semibold">{r.name}</div>
                      {r.email && <div className="text-muted small">{r.email}</div>}
                    </td>
                    <td className="small">
                      {[r.city, r.stateProvince, r.country].filter(Boolean).join(', ') || <span className="text-muted">—</span>}
                    </td>
                    <td className="text-center">
                      {r.isNational ? <span className="badge text-bg-primary">National</span> : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${r.isActive ? 'text-bg-success' : 'text-bg-secondary'}`}>{r.isActive ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="text-end small">
                      {r.activeProductCount ?? 0}<span className="text-muted">/{r.productCount ?? 0}</span>
                    </td>
                    <td className="small">
                      {!elig ? (
                        <span className="text-muted">—</span>
                      ) : elig.eligible ? (
                        <span className="text-success d-inline-flex align-items-center gap-1">
                          <ShieldCheck size={14} /> Eligible
                        </span>
                      ) : (
                        <span className="text-danger d-inline-flex align-items-center gap-1" title={`Missing: ${elig.missingAcronyms.join(', ')}`}>
                          <ShieldAlert size={14} /> Missing {elig.missingAcronyms.length}
                        </span>
                      )}
                    </td>
                    <td className="small">
                      {r.isPartner && r.subdomain ? (
                        <span
                          className="text-primary d-inline-flex align-items-center gap-1"
                          title={`${r.subdomain}.estimator.trusscore.com`}
                        >
                          <Sparkles size={14} />
                          <span className="font-monospace">{r.subdomain}</span>
                        </span>
                      ) : r.subdomain ? (
                        <span
                          className="text-muted d-inline-flex align-items-center gap-1"
                          title="Subdomain set but partner toggle is off"
                        >
                          <Sparkles size={14} />
                          <span className="font-monospace">{r.subdomain}</span>
                          <span className="badge text-bg-light ms-1">off</span>
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        title={isSuperAdmin && r.id !== 1 ? 'Edit retailer' : 'View details'}
                        onClick={() => setEditId(r.id)}
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(editId !== null || createOpen) && (
        <AdminRetailerEditModal
          retailerId={editId}
          isCreating={createOpen}
          canEdit={isSuperAdmin}
          onClose={() => { setEditId(null); setCreateOpen(false); }}
          onSaved={refresh}
          onDeleted={async () => {
            const target = retailers.find(r => r.id === editId);
            if (!target) { setEditId(null); return; }
            await handleDelete(target.id, target.name);
            setEditId(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminRetailers;
